import type { AiTaskOut, PagedAiTaskOut } from '@repo/api-codegen';
import { fetchListAiTasks } from '@repo/api-codegen';
import type { InfiniteData } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

type PageParam = { page: number };

export function useInfiniteAiTasks() {
	return useInfiniteQuery<
		PagedAiTaskOut,
		unknown,
		InfiniteData<AiTaskOut, PageParam> & { count: number },
		unknown[],
		PageParam
	>({
		queryKey: ['ai', 'tasks', 'infinite'],
		queryFn: ({ signal, pageParam }) => {
			const page = pageParam?.page ?? 1;
			return fetchListAiTasks({ queryParams: { page } }, signal);
		},
		placeholderData: keepPreviousData,
		initialPageParam: { page: 1 },
		getNextPageParam: (lastPage) =>
			lastPage.meta.next_page ? { page: lastPage.meta.next_page } : undefined,
		getPreviousPageParam: (firstPage) =>
			firstPage.meta.previous_page
				? { page: firstPage.meta.previous_page }
				: undefined,
		select: ({ pages, pageParams }) => ({
			count: pages?.[0]?.count ?? 0,
			pages: pages.flatMap((p) => p.results),
			pageParams,
		}),
	});
}

export type UseInfiniteAiTasksReturn = ReturnType<typeof useInfiniteAiTasks>;
